// src/utils/deliveryConfig.ts
import { supabase } from '../services/supabase';

export interface DeliveryConfig {
    freeDeliveryThreshold: number;  // порог в ШТУКАХ
    standardDeliveryPrice: number;   // стоимость доставки
}

// Кэш для настроек (по странам)
let deliveryConfigCache: Map<string, DeliveryConfig> = new Map();
let lastFetchTime: number = 0;
const CACHE_TTL = 60000; // 60 секунд

// Подписки на изменения
let realtimeSubscription: any = null;
let subscribers: ((config: DeliveryConfig, countryCode: string) => void)[] = [];

// Загрузка настроек из БД для конкретной страны
export const loadDeliveryConfig = async (
    countryCode: string = 'TJ',
    forceRefresh: boolean = false
): Promise<DeliveryConfig> => {
    const now = Date.now();

    // Проверяем кэш для конкретной страны
    if (!forceRefresh && deliveryConfigCache.has(countryCode) && (now - lastFetchTime) < CACHE_TTL) {
        return deliveryConfigCache.get(countryCode)!;
    }

    try {
        const { data, error } = await supabase
            .from('delivery_config')
            .select('key, value')
            .eq('country_code', countryCode)
            .eq('is_active', true);

        if (error) throw error;

        const config: DeliveryConfig = {
            freeDeliveryThreshold: 100,  // значение по умолчанию
            standardDeliveryPrice: 20     // значение по умолчанию
        };

        data?.forEach(item => {
            if (item.key === 'free_delivery_threshold') {
                config.freeDeliveryThreshold = Number(item.value);
                console.log(`[${countryCode}] Free delivery threshold:`, config.freeDeliveryThreshold);
            } else if (item.key === 'standard_delivery_price') {
                config.standardDeliveryPrice = Number(item.value);
                console.log(`[${countryCode}] Standard delivery price:`, config.standardDeliveryPrice);
            }
        });

        // Сохраняем в кэш для конкретной страны
        deliveryConfigCache.set(countryCode, config);
        lastFetchTime = now;

        notifySubscribers(config, countryCode);

        return config;
    } catch (error) {
        console.error(`Error loading delivery config for ${countryCode}:`, error);
        return {
            freeDeliveryThreshold: 100,
            standardDeliveryPrice: 20
        };
    }
};

// Расчет стоимости доставки с учетом страны
export const calculateDeliveryPriceFromDB = async (
    itemsQuantity: number,
    countryCode: string = 'TJ',
    forceRefresh: boolean = false
): Promise<number> => {
    const config = await loadDeliveryConfig(countryCode, forceRefresh);

    console.log(`=== DELIVERY PRICE CALCULATION [${countryCode}] ===`);
    console.log('Items QUANTITY:', itemsQuantity, 'pcs');
    console.log('Free delivery threshold:', config.freeDeliveryThreshold, 'pcs');
    console.log('Standard delivery price:', config.standardDeliveryPrice, 'TJS');

    if (itemsQuantity >= config.freeDeliveryThreshold) {
        console.log('✅ Delivery is FREE (>=', config.freeDeliveryThreshold, 'items)');
        return 0;
    }
    console.log('💰 Delivery price:', config.standardDeliveryPrice, 'TJS (<', config.freeDeliveryThreshold, 'items)');
    console.log('==========================================');

    return config.standardDeliveryPrice;
};

// Синхронная версия с учетом страны
export const calculateDeliveryPriceSync = (
    itemsQuantity: number,
    countryCode: string = 'TJ'
): number => {
    const config = deliveryConfigCache.get(countryCode);
    if (config) {
        if (itemsQuantity >= config.freeDeliveryThreshold) {
            return 0;
        }
        return config.standardDeliveryPrice;
    }
    // Значения по умолчанию
    return itemsQuantity >= 100 ? 0 : 20;
};

// Обновление настроек для конкретной страны
export const updateDeliveryConfig = async (
    freeDeliveryThreshold: number,
    standardDeliveryPrice: number,
    countryCode: string = 'TJ'
): Promise<boolean> => {
    try {
        // Обновляем порог бесплатной доставки
        const { error: error1 } = await supabase
            .from('delivery_config')
            .update({
                value: freeDeliveryThreshold,
                updated_at: new Date().toISOString()
            })
            .eq('key', 'free_delivery_threshold')
            .eq('country_code', countryCode);

        if (error1) throw error1;

        // Обновляем стоимость доставки
        const { error: error2 } = await supabase
            .from('delivery_config')
            .update({
                value: standardDeliveryPrice,
                updated_at: new Date().toISOString()
            })
            .eq('key', 'standard_delivery_price')
            .eq('country_code', countryCode);

        if (error2) throw error2;

        // Очищаем кэш для этой страны
        deliveryConfigCache.delete(countryCode);
        await loadDeliveryConfig(countryCode, true);

        return true;
    } catch (error) {
        console.error(`Error updating delivery config for ${countryCode}:`, error);
        return false;
    }
};

// Очистка кэша для всех стран или конкретной
export const clearDeliveryConfigCache = (countryCode?: string) => {
    if (countryCode) {
        deliveryConfigCache.delete(countryCode);
    } else {
        deliveryConfigCache.clear();
    }
    lastFetchTime = 0;
};

export const refreshDeliveryConfig = async (countryCode: string = 'TJ'): Promise<DeliveryConfig> => {
    clearDeliveryConfigCache(countryCode);
    return await loadDeliveryConfig(countryCode, true);
};

export const subscribeToDeliveryConfig = (
    callback: (config: DeliveryConfig, countryCode: string) => void
) => {
    subscribers.push(callback);

    if (!realtimeSubscription) {
        setupRealtimeSubscription();
    }

    return () => {
        subscribers = subscribers.filter(cb => cb !== callback);
        if (subscribers.length === 0 && realtimeSubscription) {
            realtimeSubscription.unsubscribe();
            realtimeSubscription = null;
        }
    };
};

const setupRealtimeSubscription = () => {
    realtimeSubscription = supabase
        .channel('delivery_config_changes')
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'delivery_config'
            },
            async (payload) => {
                console.log('Delivery config changed in real-time:', payload);
                // Очищаем кэш для затронутой страны
                if (payload.new && (payload.new as any).country_code) {
                    const countryCode = (payload.new as any).country_code;
                    clearDeliveryConfigCache(countryCode);
                    const config = await loadDeliveryConfig(countryCode, true);
                    notifySubscribers(config, countryCode);
                } else {
                    // Если не знаем страну, очищаем весь кэш
                    clearDeliveryConfigCache();
                }
            }
        )
        .subscribe((status) => {
            console.log('Realtime subscription status:', status);
        });
};

const notifySubscribers = (config: DeliveryConfig, countryCode: string) => {
    subscribers.forEach(callback => {
        try {
            callback(config, countryCode);
        } catch (error) {
            console.error('Error notifying subscriber:', error);
        }
    });
};

export const initDeliveryConfig = async (countryCode: string = 'TJ') => {
    const config = await loadDeliveryConfig(countryCode);

    subscribeToDeliveryConfig((newConfig, newCountryCode) => {
        console.log(`Delivery config updated for ${newCountryCode}:`, newConfig);
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('deliveryConfigUpdated', {
                detail: { config: newConfig, countryCode: newCountryCode }
            }));
        }
    });

    return config;
};